import { useEffect, useRef, useState } from 'react'
import { useAppStore } from '@store/appStore'
import { loadShopScrollPosition, saveShopScrollPosition } from '@utils/shopStorage'
import { ShopMetadata } from '@app-types/shopMetadata'
import { useShopCardPreview } from '@hooks/useShopCardPreview'
import { loadManifestStreaming, buildFrameUrls } from '@utils/manifestStreaming'
import { getFrameAnimationService, getShopCardPreviewAnimationService } from '@services/frameAnimationService'
import { resolveUrl } from '../../config'
import type { AnimationModeConfig } from '../../config/animationConfig'
import './ShopSelector.css'

function ShopSelector() {
  const availableShops = useAppStore((state) => state.availableShops)
  const availableDatasets = useAppStore((state) => state.availableDatasets)
  const lastSelectedShop = useAppStore((state) => state.lastSelectedShop)
  const setCurrentShop = useAppStore((state) => state.setCurrentShop)
  
  const gridRef = useRef<HTMLDivElement>(null)
  const shopRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const hoverTimeoutRef = useRef<Map<string, NodeJS.Timeout>>(new Map())
  const observerRef = useRef<IntersectionObserver | null>(null)
  const observerStartedShopRef = useRef<string | null>(null) // Track which shop was started by observer
  const currentCenterShopRef = useRef<string | null>(null) // Persist across re-renders
  
  // Track animation state per shop for pause/resume
  const animationStateRef = useRef<Map<string, {
    isAnimating: boolean
    cancelFn: (() => void) | null
  }>>(new Map())
  
  // Store shop metadata for multi-section shops
  const [shopMetadata, setShopMetadata] = useState<Map<string, ShopMetadata>>(new Map())

  // Use the shop card preview hook
  const { logShopData, previewFrame, previousFrame, previewEffects, isPreviewing, isFading, currentShopRef, startPreview, stopPreview } = useShopCardPreview()

  // Load shop metadata for all shops
  useEffect(() => {
    const loadShopMetadata = async () => {
      for (const shopId of availableShops) {
        if (!shopMetadata.has(shopId)) {
          try {
            // Get metadata path from dataset configuration (supports folder organization like NewShops/)
            const dataset = availableDatasets.find(d => d.id === shopId)
            const metadataPath = dataset?.paths?.metadata || `/api/scan-data/${shopId}/shop_metadata.json`
            const response = await fetch(resolveUrl(metadataPath))
            if (response.ok) {
              const metadata: ShopMetadata = await response.json()
              setShopMetadata(prev => new Map(prev).set(shopId, metadata))
            }
          } catch (error) {
            console.warn(`⚠️ [ShopSelector] Failed to load metadata for ${shopId}:`, error)
          }
        }
      }
    }
    
    if (availableShops.length > 0) {
      loadShopMetadata()
    }
  }, [availableShops])

  // Set up scroll listener for frame-in-middle auto-play
  useEffect(() => {
    if (shopRefs.current.size === 0) return

    // Find which shop card is closest to the center of the viewport
    const findCardInCenter = () => {
      const viewportHeight = window.innerHeight
      const viewportCenter = viewportHeight / 2
      
      let closestShopId: string | null = null
      let closestDistance = Infinity

      shopRefs.current.forEach((shopElement, shopId) => {
        const shopRect = shopElement.getBoundingClientRect()
        // Get card's center in viewport
        const shopCenterInViewport = shopRect.top + shopRect.height / 2
        // Distance from viewport's center
        const distance = Math.abs(shopCenterInViewport - viewportCenter)
        
        if (distance < closestDistance) {
          closestDistance = distance
          closestShopId = shopId
        }
      })

      return closestShopId
    }

    const handleScroll = () => {
      const centerShop = findCardInCenter()
      
      // Only trigger when center card changes
      if (centerShop === currentCenterShopRef.current) return

      console.log(`👁️ [ScrollListener] Card in center: ${centerShop} (prev: ${currentCenterShopRef.current})`)

      // Clear the scroll-started marker when moving to a new center
      observerStartedShopRef.current = null
      
      // BUGFIX: Always stop old animation when center card changes
      // (Whether it was started by scroll or hover, we need to stop it)
      if (currentCenterShopRef.current && isPreviewing) {
        console.log(`👁️ [ScrollListener] Stopping animation for ${currentCenterShopRef.current}`)
        
        // CRITICAL: Cancel animation service IMMEDIATELY
        // (Skip stopPreview() - its 300ms timeout conflicts with new preview starting)
        // startPreview() will handle state cleanup when it runs next
        getShopCardPreviewAnimationService().cancel()
      }

      // Start new animation (only if metadata is available for this shop)
      const hasMetadata = centerShop ? shopMetadata.has(centerShop) : false
      const isNotAnimating = observerStartedShopRef.current === null
      
      console.log(`👁️ [ScrollListener] Condition check: centerShop=${centerShop}, hasMetadata=${hasMetadata}, isNotAnimating=${isNotAnimating}`)
      
      if (centerShop && hasMetadata && isNotAnimating) {
        console.log(`👁️ [ScrollListener] Starting preview for ${centerShop}`)
        observerStartedShopRef.current = centerShop
        currentCenterShopRef.current = centerShop
        startPreview(centerShop, shopMetadata)
      } else {
        currentCenterShopRef.current = centerShop
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    
    // Check on initial mount
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [shopMetadata, startPreview, stopPreview])  // Preview animation is now triggered manually via debug button click only

  // console.log('🛍️ [ShopSelector] Rendering:', {
  //   shopCount: availableShops.length,
  //   datasetCount: availableDatasets.length,
  //   shops: availableShops,
  //   datasets: availableDatasets.map(d => ({ id: d.id, name: d.name })),
  //   lastSelectedShop
  // })

  // Restore scroll position and highlight lastSelectedShop on mount
  useEffect(() => {
    const scrollState = loadShopScrollPosition()
    
    if (scrollState && lastSelectedShop === scrollState.shopId) {
      // Restore scroll position using window.scrollY (scrolling happens at window level)
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollState.scrollTop, behavior: 'auto' })
        console.log(`📂 [ShopSelector] Restored scroll position to ${scrollState.scrollTop}px for ${scrollState.shopId}`)
      })
      
      // Highlight the shop card (no scrollIntoView - already at correct position)
      const shopElement = shopRefs.current.get(lastSelectedShop)
      if (shopElement) {
        shopElement.classList.add('shop-card--highlighted')
        console.log(`✨ [ShopSelector] Highlighted ${lastSelectedShop} at restored scroll position`)
      }
    }
  }, [lastSelectedShop])

  const handleShopClick = (shopId: string) => {
    console.log('🛍️ [ShopSelector] Shop clicked:', shopId)
    
    // Store shop preview image in sessionStorage for fast loading in GestureViewer
    const img = document.querySelector(`[data-shop-id="${shopId}"] .shop-card__thumbnail`) as HTMLImageElement
    if (img?.src) {
      const imageSrc = img.src
      sessionStorage.setItem(`shop-preview-${shopId}`, imageSrc)
      console.log(`📸 [ShopSelector] Stored preview image for ${shopId}: ${imageSrc.substring(0, 50)}...`)
    }
    
    // Save scroll position before navigating away
    const currentScroll = window.scrollY
    saveShopScrollPosition(shopId, currentScroll)
    
    setCurrentShop(shopId)
  }
  
  // Get dataset info for a shop
  const getDatasetInfo = (shopId: string) => {
    return availableDatasets.find(d => d.id === shopId)
  }

  // Get camera count for a shop (prefer shop metadata for multi-section shops)
  const getCameraCount = (shopId: string) => {
    const metadata = shopMetadata.get(shopId)
    if (metadata) {
      return metadata.total_cameras
    }
    
    const datasetInfo = getDatasetInfo(shopId)
    return datasetInfo?.camera_count
  }

  // Fallback to first frame if thumbnail fails
  const handleThumbnailError = (e: React.SyntheticEvent<HTMLImageElement>, shopId: string) => {
    const img = e.currentTarget
    const currentSrc = img.src
    
    // Try thumbnail.jpg → section_0/frames/frame_0000.jpg (standard thumbnail) → vite.svg
    if (currentSrc.includes('thumbnail.jpg')) {
      // All shops (multi-section and single-section) use section_0/frames/frame_0000.jpg
      // Get base path from dataset configuration to support folder organization
      const dataset = availableDatasets.find(d => d.id === shopId)
      const basePath = dataset?.paths?.metadata?.replace('/shop_metadata.json', '') || `/api/scan-data/${shopId}`
      img.src = resolveUrl(`${basePath}/section_0/frames/frame_0000.jpg`)
    } else {
      img.src = '/vite.svg'
    }
  }

  return (
    <div className="shop-selector">
      <div className="shop-selector__container">
        <div className="shop-selector__header">
          <h1 className="shop-selector__title">Bee-Mall</h1>
          <p className="shop-selector__subtitle">
            Select a shop to explore
          </p>
        </div>
        
        <div className="shop-selector__grid" ref={gridRef}>
          {availableShops.map((shopId) => {
            const datasetInfo = getDatasetInfo(shopId)
            // console.log('🛍️ [ShopSelector] Rendering shop card:', { shopId, hasDatasetInfo: !!datasetInfo, name: datasetInfo?.name })
            return (
              <div
                key={shopId}
                ref={(el) => {
                  if (el) shopRefs.current.set(shopId, el)
                }}
                className={`shop-card${lastSelectedShop === shopId ? ' shop-card--highlighted' : ''}`}
                data-shop-id={shopId}
                onClick={() => handleShopClick(shopId)}
                onMouseEnter={() => {
                  console.log(`🎬 [ShopSelector] Mouse enter ${shopId}`)
                  // Clear any pending stop timeout
                  const existingTimeout = hoverTimeoutRef.current.get(shopId)
                  if (existingTimeout) {
                    clearTimeout(existingTimeout)
                  }
                  // Mark that this is a hover-triggered preview (not observer)
                  observerStartedShopRef.current = null
                  // Start hover preview (takes priority over scroll observer)
                  startPreview(shopId, shopMetadata)
                }}
                onMouseLeave={() => {
                  console.log(`🎬 [ShopSelector] Mouse leave ${shopId}`)
                  // Delay stop by 500ms to give animation time to loop
                  // If mouse re-enters, this will be cleared
                  const timeout = setTimeout(() => {
                    console.log(`🎬 [ShopSelector] Actually stopping after leave timeout for ${shopId}`)
                    stopPreview()
                    hoverTimeoutRef.current.delete(shopId)
                  }, 500)
                  hoverTimeoutRef.current.set(shopId, timeout)
                }}
                role="button"
                tabIndex={0}
                aria-label={`${getDatasetInfo(shopId)?.name || shopId} — ${getCameraCount(shopId) ?? 'unknown'} cameras`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleShopClick(shopId)
                  }
                }}
                style={{
                  position: 'relative',
                  pointerEvents: 'auto'  // Ensure this captures all events
                }}
              >
                <div className="shop-card__thumbnail-container">
                  <img
                    className="shop-card__thumbnail"
                    src={(() => {
                      // Get base path from dataset configuration to support folder organization
                      const dataset = availableDatasets.find(d => d.id === shopId)
                      const basePath = dataset?.paths?.metadata?.replace('/shop_metadata.json', '') || `/api/scan-data/${shopId}`
                      return resolveUrl(`${basePath}/thumbnail.jpg?shop=${shopId}`)
                    })()}
                    alt={`${shopId} thumbnail`}
                    loading="lazy"
                    onError={(e) => handleThumbnailError(e, shopId)}
                  />
                  
                  {/* Preview animation overlay with cross-fade between frames */}
                  {isPreviewing && currentShopRef.current === shopId && (
                    <>
                      {/* Previous frame (fading out) */}
                      {previousFrame && (
                        <img
                          className="shop-card__preview-frame shop-card__preview-frame--previous"
                          src={previousFrame}
                          alt="Previous preview frame"
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            zIndex: 10,
                            opacity: previewEffects.previousOpacity ?? 0,
                            border: '2px solid #00FF00',
                            borderRadius: '8px',
                            pointerEvents: 'none'
                          }}
                        />
                      )}
                      
                      {/* Current frame (fading in) */}
                      {previewFrame && (
                        <img
                          className="shop-card__preview-frame"
                          src={previewFrame}
                          alt="Preview animation frame"
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            zIndex: 11,
                            opacity: previewEffects.currentOpacity ?? 1,
                            border: '2px solid #00FF00',
                            borderRadius: '8px',
                            pointerEvents: 'none'
                          }}
                        />
                      )}
                    </>
                  )}
                  
                  {/* Debug preview - always present for red button animation */}
                  <img
                    id={`debug-preview-${shopId}`}
                    alt="Debug preview animation"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      zIndex: 10,
                      opacity: 0,           // Hidden by default
                      border: '2px solid #00FF00',
                      borderRadius: '8px',
                      pointerEvents: 'none'  // Don't interfere with clicks
                    }}
                  />
                  
                  <div className="shop-card__overlay" />

                  <div className="shop-card__badge" aria-hidden="true">
                    <svg className="shop-card__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="shop-card__badge-count">{getCameraCount(shopId) !== undefined ? getCameraCount(shopId) : '—'}</span>
                  </div>

                  {datasetInfo?.is_multi_section && (
                    <div className="shop-card__section-badge" aria-hidden="true">Multi</div>
                  )}
                </div>
                <div className="shop-card__content">
                  <h3 className="shop-card__title">
                    {getDatasetInfo(shopId)?.name || shopId}
                  </h3>
                  <div className="shop-card__meta">
                    <svg className="shop-card__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>
                      {getCameraCount(shopId) !== undefined
                        ? `${getCameraCount(shopId)} cameras`
                        : 'Tap to explore'}
                    </span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        
        {availableShops.length === 0 && (
          <div className="shop-selector__empty">
            <p>No shops available</p>
            <p className="shop-selector__empty-hint">
              Add COLMAP reconstructions to /public/reconstructions/
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default ShopSelector
